/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  components as componentsLib,
  unstable
} from '@broadleaf/admin-components';
import Color from 'color';
import { get, toPairs } from 'lodash';

const { FormComponents } = componentsLib;
const { FieldDecorations } = unstable;

const ColorPaletteField = props => {
  const { metadata, formik } = props;
  const { defaultValue, name } = metadata;
  const { setFieldValue, values } = formik;
  const baseColor = get(values, `${name}.color500`);
  const components = useComponents(name);
  const palette = usePalette(baseColor, values, name);

  useEffect(() => {
    toPairs(palette).forEach(([fld, val]) => {
      // console.debug(fld, 'hsl =>', val.hsl().color, 'hsv =>', val.color);
      setFieldValue(`${name}.${fld}`, val.hex().toString().toUpperCase());
    });
  }, [name, setFieldValue, palette]);

  useEffect(() => {
    if (!baseColor) {
      let val = defaultValue ? defaultValue.toUpperCase() : defaultValue;
      setFieldValue(`${name}.color500`, val);
    }
    // eslint-disable-next-line
  }, [defaultValue, name, setFieldValue]);

  return (
    <FieldDecorations fullWidth formik={formik} metadata={metadata}>
      <FormComponents components={components} formik={formik} />
    </FieldDecorations>
  );
};

function useComponents(baseName) {
  return useMemo(
    () =>
      BaseComponents.map(component => ({
        ...component,
        name: `${baseName}.${component.name}`
      })),
    [baseName]
  );
}

const BaseComponents = Object.freeze([
  {
    classifier: 'Field',
    name: 'color100',
    type: 'AR_COLOR_PICKER',
    label: 'Lightest',
    order: 100
  },
  {
    classifier: 'Field',
    name: 'color200',
    type: 'AR_COLOR_PICKER',
    label: 'Very Light',
    order: 200
  },
  {
    classifier: 'Field',
    name: 'color300',
    type: 'AR_COLOR_PICKER',
    label: 'Lighter',
    order: 300
  },
  {
    classifier: 'Field',
    name: 'color400',
    type: 'AR_COLOR_PICKER',
    label: 'Light',
    order: 400
  },
  {
    classifier: 'Field',
    name: 'color500',
    type: 'AR_COLOR_PICKER',
    label: 'Base Color',
    defaultValue: '#1A6E99',
    hint:
      'Changing the base color will cause all variants to be updated as well',
    order: 500
  },
  {
    classifier: 'Field',
    name: 'color600',
    type: 'AR_COLOR_PICKER',
    label: 'Dark',
    order: 600
  },
  {
    classifier: 'Field',
    name: 'color700',
    type: 'AR_COLOR_PICKER',
    label: 'Darker',
    order: 700
  },
  {
    classifier: 'Field',
    name: 'color800',
    type: 'AR_COLOR_PICKER',
    label: 'Very Dark',
    order: 800
  },
  {
    classifier: 'Field',
    name: 'color900',
    type: 'AR_COLOR_PICKER',
    label: 'Darkest',
    order: 900
  }
]);

const MAX_BRIGHTNESS = 100; // max value for HSV
const MIN_BRIGHTNESS = 20; // need to stop here or darkest will probably be indistinguishable from black
const MAX_SATURATION = 100; // max saturation for HSV
const MIN_SATURATION = 5; // need to stop before hitting 0 or lightest will be white

function usePalette(base, values, name) {
  const [palette, setPalette] = useState(generatePalette(base, values, name));
  const didRender = useRef(false);

  useEffect(() => {
    if (didRender.current) {
      setPalette(generatePalette(base, {}, name));
    }

    didRender.current = true;
  }, [base, name]);

  return palette;
}

function generatePalette(base, values, name) {
  const baseColor = Color(base).hsv();

  const baseHue = baseColor.color[0];

  const baseSaturation = baseColor.color[1];
  const lightSaturation = Math.max(
    baseSaturation - baseSaturation / 5,
    MIN_SATURATION * 8
  );
  const lighterSaturation = Math.max(
    lightSaturation - lightSaturation / 4,
    MIN_SATURATION * 4
  );
  const veryLightSaturation = Math.max(
    lighterSaturation - lighterSaturation / 3,
    MIN_SATURATION * 2
  );
  const lightestSaturation = Math.max(
    veryLightSaturation - veryLightSaturation / 2,
    MIN_SATURATION
  );

  const darkSaturation = baseSaturation + (MAX_SATURATION - baseSaturation) / 2;
  const darkerSaturation =
    darkSaturation + (MAX_SATURATION - darkSaturation) / 2;
  const veryDarkSaturation =
    darkerSaturation + (MAX_SATURATION - darkerSaturation) / 2;
  const darkestSaturation =
    veryDarkSaturation + (MAX_SATURATION - veryDarkSaturation) / 2;

  const baseBrightness = baseColor.color[2];
  const lighterBrightness = // 300
    (MAX_BRIGHTNESS - baseBrightness) / 2 + baseBrightness;
  const lightBrightness = // 400
    (lighterBrightness - baseBrightness) / 2 + baseBrightness;
  const veryLightBrightness = // 200
    (MAX_BRIGHTNESS - lighterBrightness) / 2 + lighterBrightness;

  const darkerBrightness = // 700
    (baseBrightness - MIN_BRIGHTNESS) / 2 + MIN_BRIGHTNESS;
  const darkBrightness = // 600
    (baseBrightness - darkerBrightness) / 2 + darkerBrightness;
  const veryDarkBrightness = // 800
    (darkerBrightness - MIN_BRIGHTNESS) / 2 + MIN_BRIGHTNESS;

  return {
    color100: getColorOrGenerate(
      values,
      `${name}.color100`,
      Color(base).hsv(baseHue, lightestSaturation, MAX_BRIGHTNESS)
    ),
    color200: getColorOrGenerate(
      values,
      `${name}.color200`,
      Color(base).hsv(baseHue, veryLightSaturation, veryLightBrightness)
    ),
    color300: getColorOrGenerate(
      values,
      `${name}.color300`,
      Color(base).hsv(baseHue, lighterSaturation, lighterBrightness)
    ),
    color400: getColorOrGenerate(
      values,
      `${name}.color400`,
      Color(base).hsv(baseHue, lightSaturation, lightBrightness)
    ),
    color600: getColorOrGenerate(
      values,
      `${name}.color600`,
      Color(base).hsv(baseHue, darkSaturation, darkBrightness)
    ),
    color700: getColorOrGenerate(
      values,
      `${name}.color700`,
      Color(base).hsv(baseHue, darkerSaturation, darkerBrightness)
    ),
    color800: getColorOrGenerate(
      values,
      `${name}.color800`,
      Color(base).hsv(baseHue, veryDarkSaturation, veryDarkBrightness)
    ),
    color900: getColorOrGenerate(
      values,
      `${name}.color900`,
      Color(base).hsv(baseHue, darkestSaturation, MIN_BRIGHTNESS)
    )
  };
}

function getColorOrGenerate(values, name, generated) {
  if (get(values, name)) {
    return Color(get(values, name)).hsv();
  }
  return generated;
}

export default ColorPaletteField;
export { ColorPaletteField };
